import { useState } from "react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { DetailModal } from "@/components/detail-modal";
import { EntityForm } from "@/components/entity-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntentButton } from "@/components/intent-button";
import { toast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import { useConfirm } from "@/hooks/use-confirm";
import {
  priceTablesAdminApi,
  priceTableValidationApi,
  priceTableKeys,
  type PriceTableListItem,
} from "@/lib/price-tables";

// ---------------------------------------------------------------------------
// Zod schema
// ---------------------------------------------------------------------------

const schema = z.object({
  description: z.string().min(1, "Required").max(100),
});

type FormT = z.infer<typeof schema>;

const BLANK: FormT = { description: "" };

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function PriceTablesAdminPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PriceTableListItem | null>(null);

  const { has } = usePermissions();
  const canWrite = has("price-tables:write");
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: priceTableKeys.adminList(q, page),
    queryFn: () => priceTablesAdminApi.listAdmin(q, page),
  });

  // Create / rename mutation
  const upsert = useMutation({
    mutationFn: (form: FormT) => {
      if (editing) {
        return priceTablesAdminApi.update(editing.priceTableId, form, editing.rowVersion);
      }
      return priceTablesAdminApi.create(form);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["price-tables", "admin"] });
      setCreating(false);
      setEditing(null);
    },
    onError: (err: Error) => {
      toast.error(`Save failed: ${err.message}`);
    },
  });

  // Delete mutation
  const remove = useMutation({
    mutationFn: (row: PriceTableListItem) =>
      priceTablesAdminApi.remove(row.priceTableId, row.rowVersion),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["price-tables", "admin"] });
    },
    onError: (err: Error) => {
      toast.error(`Delete failed: ${err.message}`);
    },
  });

  const validation = useQuery({
    queryKey: priceTableKeys.validation(),
    queryFn: () => priceTableValidationApi.get(),
  });
  const findings = validation.data?.findings ?? [];
  const flaggedIds = new Set(findings.map((f) => f.priceTableId));

  // Columns
  const cols: DataGridColumn<PriceTableListItem>[] = [
    { id: "id", header: "PT#", accessor: (r) => r.priceTableId, width: "64px" },
    { id: "description", header: "Description", accessor: (r) =>
        flaggedIds.has(r.priceTableId)
          ? <span>{r.description} <span title="Has validation issues" className="text-destructive">⚠</span></span>
          : r.description },
    { id: "rowCount", header: "Rows", accessor: (r) => r.rowCount, align: "right", width: "80px" },
    { id: "rateTypes", header: "Rate types", accessor: (r) => r.rateTypesUsed ?? "—" },
    {
      id: "actions",
      header: "",
      accessor: (r) => (
        <div className="flex gap-1">
          <Button asChild variant="outline" size="sm">
            <Link to={`/admin/bom/price-tables/${r.priceTableId}`}>Edit rates</Link>
          </Button>
          {canWrite && (
            <IntentButton intent="ghost" onClick={() => setEditing(r)}>
              Rename
            </IntentButton>
          )}
          {canWrite && (
            <IntentButton
              intent="ghost"
              onClick={() => {
                void confirm({
                  title: "Delete price table?",
                  body: r.description,
                  confirmLabel: "Delete",
                  intent: "destructive",
                }).then((ok) => { if (ok) remove.mutate(r); });
              }}
            >
              Delete
            </IntentButton>
          )}
        </div>
      ),
    },
  ];

  function toFormValues(row: PriceTableListItem): FormT {
    return { description: row.description };
  }

  const drawerOpen = creating || editing !== null;
  const drawerTitle = editing ? "Rename price table" : "New price table";

  return (
    <ListPage backTo="/admin"
      title="Price tables"
      primaryAction={
        canWrite ? (
          <Button onClick={() => setCreating(true)}>+ New price table</Button>
        ) : undefined
      }
      search={{
        value: q,
        onChange: (v) => {
          setQ(v);
          setPage(0);
        },
        placeholder: "Search description…",
      }}
    >
      {findings.length > 0 && (
        <div
          role="alert"
          className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm"
        >
          <p className="font-medium text-destructive">
            {findings.length} validation {findings.length === 1 ? "issue" : "issues"} found
            {validation.data?.checkedAtUtc
              ? ` (last checked ${new Date(validation.data.checkedAtUtc).toLocaleString()})`
              : ""}
          </p>
          <ul className="mt-1 list-disc pl-5 text-muted-foreground">
            {findings.map((f, i) => (
              <li key={i}>
                <span className="font-medium">{f.priceTableDescription}</span> — {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      <DataGrid
        rows={data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.priceTableId}
        totalRows={data?.total ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        onPageChange={setPage}
        emptyHeadline="No price tables yet"
      />

      <DetailModal
        open={drawerOpen}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
        title={drawerTitle}
      >
        <EntityForm
          key={editing?.priceTableId ?? "new"}
          schema={schema}
          defaultValues={editing ? toFormValues(editing) : BLANK}
          onSubmit={(v) => upsert.mutate(v)}
        >
          {(form) => (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="pt-description">Description</Label>
                <Input id="pt-description" {...form.register("description")} />
                {form.formState.errors.description && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.description.message}
                  </p>
                )}
              </div>
              <Button type="submit">Save</Button>
            </div>
          )}
        </EntityForm>
      </DetailModal>
    </ListPage>
  );
}
